import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { mdiClose } from '@mdi/js';
import { Dialog, DialogTitle, ButtonBase, DialogContent } from '@mui/material';
import { Icon } from '@mdi/react';
import { Box } from '@mui/system';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { toast } from 'react-toastify';
import { defineStyles } from '../../theme';
import { ModificationsForm } from '../ModificationsForm';
import { useSTContext } from '../../table/SmartTableContext';
import DefaultBackdrop from '../DefaultBackdrop';
const styles = defineStyles({
    root: {
        height: '100%',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    headerText: {
        display: 'flex',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
});
export const NewRowDialog = ({ open, onClose, invalidateTable, visibleColumns, addRowApiCall, allowNewColumns, renderExtraContent, }) => {
    const [isLoading, setIsLoading] = useState(false);
    const table = useSTContext();
    const prepareNewRow = table.prepareNewRow;
    const [prepareData, setPrepareData] = useState(undefined);
    useEffect(() => {
        if (!open || !prepareNewRow) {
            setPrepareData(undefined);
            return;
        }
        let cancelled = false;
        prepareNewRow()
            .then((data) => {
            if (!cancelled)
                setPrepareData(data);
        })
            .catch(() => {
            if (!cancelled)
                setPrepareData(undefined);
        });
        return () => {
            cancelled = true;
        };
    }, [open, prepareNewRow]);
    const insertableColumns = useMemo(() => visibleColumns.filter((column) => column.insertable), [visibleColumns]);
    const fieldKeyToFieldName = useMemo(() => {
        const map = new Map();
        insertableColumns.forEach((column) => {
            map.set(column.fieldKey, column.fieldName);
        });
        return map;
    }, [insertableColumns]);
    const formDefaultValues = prepareData?.defaultValues;
    const handleClose = useCallback((_, reason) => {
        if (reason === 'backdropClick') {
            return;
        }
        onClose();
    }, [onClose]);
    const handleSubmit = useCallback(async (record) => {
        setIsLoading(true);
        try {
            const apiRecord = {};
            Object.entries(record).forEach(([fieldKey, value]) => {
                const fieldName = fieldKeyToFieldName.get(fieldKey) ?? fieldKey;
                if (value !== undefined && value !== null) {
                    apiRecord[fieldName] = value;
                }
            });
            const result = await addRowApiCall(apiRecord);
            invalidateTable();
            onClose();
            if (result?.ipsosId != null) {
                toast(_jsx(FormattedMessage, { id: "newRowDialog.successWithIpsosId", defaultMessage: "Row added with ipsosId: {ipsosId}", values: { ipsosId: result.ipsosId } }), { type: 'success' });
            }
        }
        catch (error) {
            console.error(error);
            toast(_jsx(FormattedMessage, { ...error }), {
                type: 'error',
            });
        }
        finally {
            setIsLoading(false);
        }
    }, [addRowApiCall, invalidateTable, onClose, fieldKeyToFieldName]);
    return (_jsxs(_Fragment, { children: [_jsxs(Dialog, { open: open, maxWidth: "sm", fullWidth: true, onClose: handleClose, sx: styles.root, children: [_jsxs(DialogTitle, { sx: styles.header, children: [_jsx(Box, { sx: styles.headerText, children: _jsx(FormattedMessage, { id: "newRowDialog.title", defaultMessage: "New row" }) }), _jsx(ButtonBase, { onClick: onClose, disableRipple: true, children: _jsx(Icon, { path: mdiClose, size: 1 }) })] }), _jsx(DialogContent, { sx: styles.content, children: open ? (_jsx(ModificationsForm, { onSubmit: handleSubmit, onCancel: onClose, visibleColumns: insertableColumns, allowNewColumns: allowNewColumns, defaultValues: formDefaultValues, fieldOverrides: table.newRowFieldOverrides, fieldPrepareData: prepareData?.fieldPrepareData, renderExtraContent: renderExtraContent }, `${table.tableKey}-new-row`)) : null })] }), _jsx(DefaultBackdrop, { isLoading: isLoading })] }));
};
//# sourceMappingURL=NewRowDialog.js.map